import type { TaskResultSqlOutput } from './evalTypes';

/**
 * Detect whether the pasted text is tab-separated or comma-separated.
 * Tabs take priority because Excel always copies as TSV.
 */
function detectDelimiter(text: string): string {
    const firstLine = text.split(/\r?\n/)[0] ?? '';
    return firstLine.includes('\t') ? '\t' : ',';
}

/**
 * Split a single CSV/TSV row respecting quoted fields.
 * Handles double-quote escaping (RFC 4180).
 */
function splitRow(line: string, delimiter: string): string[] {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"' && line[i + 1] === '"') {
                current += '"';
                i++;
            } else if (ch === '"') {
                inQuotes = false;
            } else {
                current += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === delimiter) {
            cells.push(current.trim());
            current = '';
        } else {
            current += ch;
        }
    }
    cells.push(current.trim());
    return cells;
}

function inferValue(raw: string): unknown {
    if (raw === '') return null;
    const num = Number(raw);
    if (!Number.isNaN(num) && raw !== '') return num;
    if (raw.toLowerCase() === 'true') return true;
    if (raw.toLowerCase() === 'false') return false;
    return raw;
}

/**
 * Parse pasted text (tab-separated from Excel, or comma-separated) into
 * `TaskResultSqlOutput` format.  First row is treated as column headers.
 */
export function parseTableData(text: string): TaskResultSqlOutput | null {
    const trimmed = text.trim();
    if (!trimmed) return null;

    const delimiter = detectDelimiter(trimmed);
    const lines = trimmed.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 1) return null;

    const columns = splitRow(lines[0], delimiter);
    if (columns.length === 0 || columns.every((c) => !c)) return null;

    const rows: Record<string, unknown>[] = [];
    for (let i = 1; i < lines.length; i++) {
        const cells = splitRow(lines[i], delimiter);
        const row: Record<string, unknown> = {};
        columns.forEach((col, idx) => {
            row[col] = inferValue(cells[idx] ?? '');
        });
        rows.push(row);
    }

    return { columns, rows };
}
